import { Form } from 'react-bootstrap';
import {
  Alert,
  BaseModal,
  Button,
  RadioButton,
  TextInput,
} from '@pmi/dsm-react';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';

import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { BaseInternalAdmin } from '../models';
import {
  useInviteInternalAdminMutation,
  useUpdateInternalAdminMutation,
} from './apiSlice';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import permissions from '../../../../../shared/permissions';
import { toastWithAnalytics } from '../../../../../shared/analytics';
import {
  normalizeEmail,
  singleEmailRegex,
} from '../../../../../shared/helpers';
import styles from './inviteEditInternalAdminModal.module.scss';

interface InviteEditInternalAdminModalProps {
  admin: BaseInternalAdmin;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export default function InviteEditInternalAdminModal({
  admin,
  onCancel = () => {},
  onSuccess = () => {},
}: InviteEditInternalAdminModalProps) {
  const { personId, accountUserId, inviteCode, invitationEmail, role } = admin;
  const [email, setEmail] = useState(admin.invitationEmail);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(role || 'Admin');
  const [initRole, setInitRole] = useState('');
  const [roleError, setRoleError] = useState(false);
  const formEl = useRef<HTMLFormElement>(undefined);
  const [inviteInternalAdmin, invitedInternalAdmin] =
    useInviteInternalAdminMutation();
  const [updateInternalAdmin, updatedInternalAdmin] =
    useUpdateInternalAdminMutation();
  const { hasAccess } = usePermissions();
  const isDev = hasAccess({ permissions: [permissions.ManagePMIDeveloper] });
  const onRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedRole(event.target.value);
  };
  const onSubmit = () => {
    const normalized = normalizeEmail(email);
    const isValid =
      singleEmailRegex.test(normalized) &&
      normalized.toLocaleLowerCase().endsWith('@pmi.org');
    setIsEmailValid(isValid);
    if (!isValid) return;

    setRoleError(false);
    if (!selectedRole || selectedRole.length === 0) {
      setRoleError(true);
      return;
    }

    let action = null;
    let actioned: any = null;
    if (admin.inviteCode || admin.id > 0) {
      action = updateInternalAdmin({
        id: admin.id,
        roleType: selectedRole,
        personId,
        inviteCode,
        accountUserId,
        email: admin.inviteCode ? normalized : invitationEmail,
      });
      actioned = updatedInternalAdmin;
    } else {
      // const possiblePersonId = parseInt(usernameOrEmail, 10);
      // const isPersonId = !Number.isNaN(possiblePersonId);

      action = inviteInternalAdmin({
        email: normalized,
        roleType: selectedRole,
      });
      actioned = invitedInternalAdmin;
    }

    setLoading(true);
    action
      .unwrap()
      .then(() => {
        actioned.reset();
        setLoading(false);
        if (!admin.personId) {
          const noPersonId = (): ReactNode => (
            <>
              You have invited <span data-exclude-lg>{email}</span>
            </>
          );
          toastWithAnalytics(noPersonId(), 'success');
        } else {
          const message = (): ReactNode => (
            <>
              <span data-exclude-lg="true">
                {admin.name} || ${email}
              </span>{' '}
              has been updated
            </>
          );
          toastWithAnalytics(message(), 'success');
        }
        setRoleError(false);
        onSuccess();
      })
      .catch((ex) => {
        let error = ex.data.detail;

        try {
          // check if api returned an error
          let apiError = JSON.parse(ex.data.Value);
          apiError = apiError?.detail;
          if (apiError) error = apiError;
        } catch (exception) {
          // swallow parse ex
        }

        toastWithAnalytics(`User could not be updated. ${error}`, 'danger');
        setLoading(false);
        setRoleError(false);
      });
  };
  const onCancelHandler = () => {
    onCancel();
  };

  useEffect(() => {
    setInitRole(personId ? role : '');
  }, []);

  return (
    <BaseModal
      dialogClassName="dsm"
      backdropClassName="dsm"
      show
      scrollable={false}
      size="lg"
      className="inviteInternalAdmin"
    >
      <BaseModal.Header>
        <BaseModal.Title>{admin.personId ? 'Edit' : 'Invite'}</BaseModal.Title>
      </BaseModal.Header>

      <BaseModal.Body className="text-start">
        <form onSubmit={onSubmit} ref={formEl} data-testid="invite-form">
          <Form.Group controlId="email">
            <Form.Label>Please enter a valid PMI email address</Form.Label>
            <TextInput
              className={isEmailValid ? '' : 'is-invalid'}
              validationMessage="Please enter a valid email address."
              isInvalid={!isEmailValid}
              value={email}
              onChange={(e: FormEvent<HTMLInputElement>) =>
                setEmail(e.currentTarget?.value)
              }
              id="email"
              data-testid="email-input"
              name="email"
              disabled={!!personId || !!inviteCode || loading}
            />
          </Form.Group>

          <div>
            <Form.Label className="mb-2 mt-3">Select Role:</Form.Label>
            <div className="border rounded p-3 mb-4">
              <RadioButton
                onChange={onRoleChange}
                inputProps={{
                  'aria-label': 'Administrator',
                  'data-testid': 'administrator-role-radio',
                }}
                name="radio-buttons-roles"
                value="Admin"
                label={
                  <div className="ms-2">
                    <div
                      className={`body3 fw-bold text-link text-link--primary ${styles.textLeftAlign}`}
                    >
                      PMI Admin
                    </div>
                    <div className="body4">
                      Users with this role can view and modify all account
                      settings and info.
                    </div>
                  </div>
                }
                id="administrator-role-radio"
                checked={
                  selectedRole === 'Admin' || selectedRole === 'B2B:PMIADM'
                }
                disabled={loading}
              />
            </div>
            {isDev && (
              <div className="border rounded p-3 mb-4">
                <RadioButton
                  onChange={onRoleChange}
                  inputProps={{
                    'aria-label': 'Developer',
                    'data-testid': 'developer-role-radio',
                  }}
                  name="radio-buttons-roles"
                  value="Dev"
                  label={
                    <div className="ms-2">
                      <div
                        className={`body3 fw-bold text-link text-link--primary ${styles.textLeftAlign}`}
                      >
                        Developer
                      </div>
                      <div className="body4">
                        Users with this role can view and modify all account
                        settings and info.
                      </div>
                    </div>
                  }
                  id="developer-role-radio"
                  checked={
                    selectedRole === 'Dev' || selectedRole === 'B2B:PMIDEV'
                  }
                  disabled={loading}
                />
              </div>
            )}
            {roleError && (
              <Alert
                variant="danger"
                alertText="Please select role to proceed"
                icon={IconDanger}
              />
            )}
          </div>
        </form>
      </BaseModal.Body>

      <BaseModal.Footer>
        <Button
          variant="outline-secondary"
          disabled={loading}
          titleText="Cancel"
          onClick={onCancelHandler}
          data-testid="cancel-button"
        />
        <Button
          variant="primary"
          disabled={loading || initRole === selectedRole}
          onClick={onSubmit}
          titleText={personId ? 'Update' : 'Invite'}
          data-testid="submit-button"
        />
      </BaseModal.Footer>
    </BaseModal>
  );
}
